import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import 'react-toastify/dist/ReactToastify.css';
import ViewCompanyModal from '../../components/ViewCompanyModal';
import Select from 'react-select'; // Add this import


const DomainAccessManager = ({ domains, setDomains, defaultAccess }) => {
  // Helper function to format option labels
  const formatLabel = (option) => {
    return option.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to create grouped options
  const createGroupedOptions = (accessType) => {
    const groupMap = {
      admin: {
        'Core Operations': ['delivery-order', 'items', 'quotation', 'crm'],
        'Tally Integration': ['tally-quotation', 'tally-items'],
        'Management & Reports': ['activity-log', 'campaign-management', 'schemes-reports', 'tally-reports'],
      },
      companyHead: {
        'Dashboard & Core': ['dashboard'],
        'HR Management': ['employee-management', 'leave-management', 'attendance-management', 'payroll-management', 'role-management'],
        'Asset & Brand Management': ['asset-management', 'brand-management'],
        'System Management': ['tally-management', 'tiles-management', 'stock-items-management'],
        'App Dealer Management': ['app-dealer-management', 'app-dealer-access-logs', 'app-dealer-interest-reports'],
        'Reports & Analytics': ['delivery-orders-reports', 'activity-logs-reports', 'choice-entry-reports', 'quotations-reports', 'proforma-invoice-reports', 'showroom-analytics', 'delivery-orders-detailed-reports', 'goods-return-reports', 'tally-reports'],
        'Financial': ['tiles-day-book', 'tiles-ledger'],
        'Automations': ['whatsapp-automations'],
      },
      employee: {
        'Dashboard & Core': ['dashboard'],
        'Core Operations': ['delivery-order',  'quotation', 'quotation-new', 'estimate', 'proforma-invoice', 'packing-slip', 'goods-received'],
        'Customer & Sales': ['tele-calling-payment', 'customer-management', 'dealer-management', 'buyers', 'supplier-management', 'guest-master', 'visitor-management', 'reference-management', 'ma-management'],
        'Inventory & Items': ['item-management', 'purchase-indent', 'reference-commission'],
        'HR & Personal': ['apply-leave', 'attendance', 'field-visit', 'reminders'],
        'Activity & Logs': ['activity-log', 'activity-logs-new', 'choice-entry'],
        'Reports & Analytics': ['quotations-reports', 'management-reports'],
        'Location & Transport': ['station-management', 'transport-management'],
        'Doors Management': ['doors', 'doors-inward', 'doors-outward', 'doors-stock-status', 'doors-parameters'],
        'Tiles Management': ['tiles-master', 'tiles-parameters', 'tiles-stock-status', 'tiles-inward', 'tiles-outward', 'tiles-delivery-order', 'tiles-sales-order', 'tiles-location-transfer', 'tiles-day-book', 'tiles-ledger','tiles-dispatch-entry', 'tiles-order', 'tiles-received', 'tiles-return'],
        'Tally Integration': ['tally-quotation', 'tally-items-management', 'tally-parameters-management', 'tally-ledger-management', 'tally-operations-panel'],
        'Display Panels': ['display-panel-management'],
        'Stock Management': ['stock-master', 'stock-items-inward', 'stock-items-outward', 'stock-items-location-transfer', 'stock-items-status', 'stock-items-ledger', 'stock-items-day-book', 'stock-movement', 'stock-items-movement', 'stock-items-order', 'stock-items-dispatch', 'stock-items-received', 'stock-items-destroy'],
        'Product & Inventory': ['concept-product', 'demolished-tiles'],
        'Returns': ['goods-return'],
        'PDF Catalogs': ['pdf-catalogs']
      }
    };

    const groups = groupMap[accessType] || {};
    return Object.entries(groups).map(([groupName, options]) => ({
      label: groupName,
      options: options.filter(opt => {
        if (defaultAccess[accessType]?.includes(opt)) return true;
        if (accessType === 'employee' && (opt === 'quotations-reports' || opt === 'management-reports')) return true;
        return false;
      }).map(option => ({
        value: option,
        label: formatLabel(option)
      }))
    })).filter(group => group.options.length > 0);
  };

  // Helper function to handle select all/clear all
  const handleBulkSelection = (domainIndex, accessType, action) => {
    const newDomains = [...domains];
    if (action === 'selectAll') {
      switch (accessType) {
        case 'admin':
          newDomains[domainIndex].adminDashboardAccess = [...defaultAccess.admin];
          break;
        case 'companyHead':
          newDomains[domainIndex].companyHeadDashboardAccess = [...defaultAccess.companyHead];
          break;
        case 'employee':
          newDomains[domainIndex].employeeDashboardAccess = [...defaultAccess.employee];
          break;
      }
    } else if (action === 'clearAll') {
      switch (accessType) {
        case 'admin':
          newDomains[domainIndex].adminDashboardAccess = [];
          break;
        case 'companyHead':
          newDomains[domainIndex].companyHeadDashboardAccess = [];
          break;
        case 'employee':
          newDomains[domainIndex].employeeDashboardAccess = [];
          break;
      }
    }
    setDomains(newDomains);
  };

  const addDomain = () => {
    setDomains([
      ...domains,
      {
        domainName: '',
        adminDashboardAccess: [],
        companyHeadDashboardAccess: [],
        employeeDashboardAccess: [],
      },
    ]);
  };

  const removeDomain = (index) => {
    if (domains.length <= 1) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'At least one domain is required.',
      });
      return;
    }

    const newDomains = [...domains];
    newDomains.splice(index, 1);
    setDomains(newDomains);
  };

  const updateDomainName = (index, value) => {
    const newDomains = [...domains];
    newDomains[index].domainName = value;
    setDomains(newDomains);
  };



  return (
    <div className="mt-6 col-span-4 border rounded p-4 bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Domain Configuration</h3>
        <button
          type="button"
          onClick={addDomain}
          className="px-3 py-1 bg-blue-600 text-white rounded flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Domain
        </button>
      </div>

      {domains.map((domain, index) => (
        <div key={index} className="mb-6 p-4 bg-white border rounded shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <div className="flex-grow mr-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domain Name *
              </label>
              <input
                type="text"
                value={domain.domainName}
                onChange={(e) => updateDomainName(index, e.target.value)}
                className="w-full p-2 border rounded"
                required
                placeholder="example.com"
              />
            </div>
            {domains.length > 1 && (
              <button
                type="button"
                onClick={() => removeDomain(index)}
                className="px-3 py-1 bg-red-600 text-white rounded h-10 mt-6 flex items-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-700">Dashboard Access for {domain.domainName || 'this domain'}</h4>
              <div className="text-sm bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                <span className="text-blue-800 font-medium">
                  Total Features: {(domain.adminDashboardAccess?.length || 0) + 
                                 (domain.companyHeadDashboardAccess?.length || 0) + 
                                 (domain.employeeDashboardAccess?.length || 0)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="border p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-700 flex items-center">
                    Admin Dashboard Access
                    <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                      {domain.adminDashboardAccess?.length || 0}/{defaultAccess.admin.length}
                    </span>
                  </h5>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'admin', 'selectAll')}
                      className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      title="Select all admin features"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'admin', 'clearAll')}
                      className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Clear all admin features"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <Select
                  isMulti
                  name={`admin-access-${index}`}
                  options={createGroupedOptions('admin')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={domain.adminDashboardAccess?.map(option => ({
                    value: option,
                    label: formatLabel(option)
                  })) || []}
                  onChange={(selectedOptions) => {
                    const newDomains = [...domains];
                    newDomains[index].adminDashboardAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setDomains(newDomains);
                  }}
                  placeholder="Search and select admin dashboard features..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#9ca3af' },
                      '&:focus-within': { borderColor: '#ef4444', boxShadow: '0 0 0 1px #ef4444' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#ef4444',
                      borderRadius: '0.375rem',
                      border: '1px solid #dc2626',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      borderRadius: '0 0.375rem 0.375rem 0',
                      '&:hover': {
                        backgroundColor: '#dc2626',
                        color: 'white',
                      },
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '8px 12px',
                      margin: 0,
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.875rem',
                      paddingLeft: '24px',
                      backgroundColor: state.isSelected ? '#ef4444' : state.isFocused ? '#fef2f2' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#ef4444' : '#fef2f2',
                      }
                    }),
                  }}
                  closeMenuOnSelect={false}
                  isClearable={true}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Selected: {domain.adminDashboardAccess?.length || 0} of {defaultAccess.admin.length}
                </div>
              </div>

              <div className="border p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-700 flex items-center">
                    Company Head Dashboard Access
                    <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                      {domain.companyHeadDashboardAccess?.length || 0}/{defaultAccess.companyHead.length}
                    </span>
                  </h5>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'companyHead', 'selectAll')}
                      className="text-xs px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                      title="Select all company head features"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'companyHead', 'clearAll')}
                      className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Clear all company head features"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <Select
                  isMulti
                  name={`companyHead-access-${index}`}
                  options={createGroupedOptions('companyHead')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={domain.companyHeadDashboardAccess?.map(option => ({
                    value: option,
                    label: formatLabel(option)
                  })) || []}
                  onChange={(selectedOptions) => {
                    const newDomains = [...domains];
                    newDomains[index].companyHeadDashboardAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setDomains(newDomains);
                  }}
                  placeholder="Search and select company head dashboard features..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#9ca3af' },
                      '&:focus-within': { borderColor: '#f59e0b', boxShadow: '0 0 0 1px #f59e0b' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#f59e0b',
                      borderRadius: '0.375rem',
                      border: '1px solid #d97706',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      borderRadius: '0 0.375rem 0.375rem 0',
                      '&:hover': {
                        backgroundColor: '#d97706',
                        color: 'white',
                      },
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '8px 12px',
                      margin: 0,
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.875rem',
                      paddingLeft: '24px',
                      backgroundColor: state.isSelected ? '#f59e0b' : state.isFocused ? '#fef3c7' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#f59e0b' : '#fef3c7',
                      }
                    }),
                  }}
                  closeMenuOnSelect={false}
                  isClearable={true}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Selected: {domain.companyHeadDashboardAccess?.length || 0} of {defaultAccess.companyHead.length}
                </div>
              </div>

              <div className="border p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-700 flex items-center">
                    Employee Dashboard Access
                    <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                      {domain.employeeDashboardAccess?.length || 0}/{defaultAccess.employee.length}
                    </span>
                  </h5>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'employee', 'selectAll')}
                      className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      title="Select all employee features"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'employee', 'clearAll')}
                      className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Clear all employee features"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <Select
                  isMulti
                  name={`employee-access-${index}`}
                  options={createGroupedOptions('employee')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={domain.employeeDashboardAccess?.map(option => ({
                    value: option,
                    label: formatLabel(option)
                  })) || []}
                  onChange={(selectedOptions) => {
                    const newDomains = [...domains];
                    newDomains[index].employeeDashboardAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setDomains(newDomains);
                  }}
                  placeholder="Search and select employee dashboard features..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#9ca3af' },
                      '&:focus-within': { borderColor: '#10b981', boxShadow: '0 0 0 1px #10b981' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#10b981',
                      borderRadius: '0.375rem',
                      border: '1px solid #059669',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      borderRadius: '0 0.375rem 0.375rem 0',
                      '&:hover': {
                        backgroundColor: '#059669',
                        color: 'white',
                      },
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '8px 12px',
                      margin: 0,
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.875rem',
                      paddingLeft: '24px',
                      backgroundColor: state.isSelected ? '#10b981' : state.isFocused ? '#d1fae5' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#10b981' : '#d1fae5',
                      }
                    }),
                  }}
                  closeMenuOnSelect={false}
                  isClearable={true}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Selected: {domain.employeeDashboardAccess?.length || 0} of {defaultAccess.employee.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TermsAndConditionsManager = ({ title, terms, setTerms, placeholder = "Enter term or condition" }) => {
  const addTerm = () => {
    setTerms([...terms, ""]);
  };

  const removeTerm = (index) => {
    const newTerms = terms.filter((_, i) => i !== index);
    setTerms(newTerms);
  };

  const updateTerm = (index, value) => {
    const newTerms = [...terms];
    newTerms[index] = value;
    setTerms(newTerms);
  };

  return (
    <div className="col-span-4 border p-4 bg-gray-50 rounded mt-2">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button
          type="button"
          onClick={addTerm}
          className="px-3 py-1 bg-green-600 text-white rounded flex items-center hover:bg-green-700 transition-colors"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Term
        </button>
      </div>
      
      {terms.length === 0 ? (
        <div className="text-center p-4 bg-blue-50 rounded">
          <p className="text-gray-600">No terms added yet. Click "Add Term" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {terms.map((term, index) => (
            <div key={index} className="flex items-center gap-3 bg-white p-3 rounded border">
              <span className="text-sm font-medium text-gray-500 min-w-[2rem]">{index + 1}.</span>
              <input
                type="text"
                value={term}
                onChange={(e) => updateTerm(index, e.target.value)}
                placeholder={placeholder}
                className="flex-1 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => removeTerm(index)}
                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                title="Remove term"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      
      {terms.length > 0 && (
        <div className="mt-3 text-sm text-gray-600">
          Total terms: {terms.length}
        </div>
      )}
    </div>
  );
};

const USAGE_FIELD_OPTIONS = [
  { key: 'date', label: 'Date' },
  { key: 'voucherNumber', label: 'Voucher No' },
  { key: 'partyName', label: 'Party' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'status', label: 'Status' },
  { key: 'amount', label: 'Amount' },
];

const UsageVoucherTypeSelector = ({ companyId, usageVoucherTypes, onChange }) => {
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (companyId) {
      fetchVoucherTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchVoucherTypes = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tally/voucher-types/${companyId}/with-inventory`);
      if (response.data.success) {
        setVoucherTypes(response.data.data);
      } else {
        toast.error('Failed to fetch voucher types');
      }
    } catch (error) {
      console.error('Error fetching voucher types:', error);
      toast.error('Failed to fetch voucher types');
    } finally {
      setLoading(false);
    }
  };

  const toggleVoucherType = (vt) => {
    const exists = usageVoucherTypes.some(c => c.voucherType === vt._id);
    if (exists) {
      onChange(usageVoucherTypes.filter(c => c.voucherType !== vt._id));
    } else {
      onChange([...usageVoucherTypes, { voucherType: vt._id, fields: [] }]);
    }
  };

  const toggleField = (voucherTypeId, fieldKey) => {
    onChange(usageVoucherTypes.map(c => {
      if (c.voucherType !== voucherTypeId) return c;
      const fields = c.fields || [];
      return {
        ...c,
        fields: fields.includes(fieldKey)
          ? fields.filter(f => f !== fieldKey)
          : [...fields, fieldKey],
      };
    }));
  };

  const isChecked = (vtId) => usageVoucherTypes.some(c => c.voucherType === vtId);
  const getFields = (vtId) => {
    const cfg = usageVoucherTypes.find(c => c.voucherType === vtId);
    return cfg ? cfg.fields || [] : [];
  };

  return (
    <div className="col-span-4 border p-4 bg-gray-50 rounded mt-2">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Voucher Types for Tally Item Usage</h3>
      {loading ? (
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
          <span className="ml-2">Loading voucher types...</span>
        </div>
      ) : voucherTypes.length > 0 ? (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            Select voucher types that contain tally item data. Their vouchers will show usage of an item in the item detail screen. For each voucher type, pick which fields to display.
          </p>
          <div className="space-y-2">
            {voucherTypes.map(vt => {
              const selected = isChecked(vt._id);
              return (
                <div key={vt._id} className={`bg-white rounded border p-3 ${selected ? 'border-indigo-300' : 'border-gray-200'}`}>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleVoucherType(vt)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {vt.parentName ? `${vt.Name} (${vt.parentName})` : vt.Name}
                    </span>
                  </label>
                  {selected && (
                    <div className="mt-2 ml-6 flex flex-wrap gap-3">
                      {USAGE_FIELD_OPTIONS.map(opt => {
                        const fields = getFields(vt._id);
                        return (
                          <label key={opt.key} className="flex items-center space-x-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={fields.includes(opt.key)}
                              onChange={() => toggleField(vt._id, opt.key)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-xs text-gray-600">{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-sm text-blue-600">{usageVoucherTypes.length} voucher type(s) selected</p>
        </div>
      ) : (
        <div className="text-center p-4 bg-blue-50 rounded">
          <p>No voucher types with inventory data found. Sync vouchers with inventory items from Tally first.</p>
          <button
            onClick={fetchVoucherTypes}
            className="mt-2 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};





const KPI_FORMATS = [
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'number', label: 'Number' },
];

const MEASURE_TYPES = [
  { value: 'sumVoucherField', label: 'Sum Voucher Field' },
  { value: 'sumLedgerAmount', label: 'Sum Ledger Amount' },
  { value: 'sumItemAmount', label: 'Sum Item Amount' },
  { value: 'sumLedgerBalance', label: 'Ledger Group Balance' },
  { value: 'sumUdf', label: 'Sum Numeric UDF' },
  { value: 'count', label: 'Voucher Count' },
  { value: 'derived', label: 'Derived (formula)' },
];

const CONDITION_FIELDS = [
  { value: 'voucherTypeName', label: 'Voucher Type' },
  { value: 'voucherNumber', label: 'Voucher Number' },
  { value: 'partyName', label: 'Party Name' },
  { value: 'totalAmount', label: 'Total Amount' },
  { value: 'totalAmountBeforeTax', label: 'Amount Before Tax' },
  { value: 'voucherStatus', label: 'Voucher Status' },
  { value: 'isCancelled', label: 'Is Cancelled' },
  { value: 'partyGSTIN', label: 'Party GSTIN' },
  { value: 'placeOfSupply', label: 'Place Of Supply' },
  { value: 'udf', label: 'UDF' },
];

const CONDITION_OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in (comma list)' },
];

const FORMULA_OPS = [
  { value: '+', label: '+ Add' },
  { value: '-', label: '- Subtract' },
  { value: '%', label: '% Percent of' },
  { value: '/', label: '/ Divide by' },
  { value: '×', label: '× Multiply constant' },
];

const DEFAULT_TALLY_REPORT_SETTINGS = { enabled: false, reports: [] };

const TallyReportSettingsManager = ({ companyId, value, onChange }) => {
  const settings = value || DEFAULT_TALLY_REPORT_SETTINGS;
  const [options, setOptions] = useState({ voucherTypes: [], udfFields: [] });
  const [optionsError, setOptionsError] = useState('');

  useEffect(() => {
    const fetchOptions = async () => {
      if (!companyId) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reports/tally/options`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );
        setOptions(res.data || { voucherTypes: [], udfFields: [] });
        setOptionsError('');
      } catch (e) {
        console.error('Failed to load tally report options', e);
        setOptionsError(
          e.response?.data?.message ||
            'Could not load voucher types / UDFs. Restart the backend to load the new tally report routes.'
        );
      }
    };
    fetchOptions();
  }, [companyId]);

  const reports = settings.reports || [];
  const update = (patch) => onChange({ ...settings, ...patch });
  const vtOptions = (options.voucherTypes || []).map((o) => ({ value: o._id, label: o.Name }));

  const uniqueKey = (base, existing) => {
    const keys = new Set(existing);
    let key = base;
    let i = 2;
    while (keys.has(key)) key = `${base}_${i++}`;
    return key;
  };

  // UDF select options, optionally numeric-only and narrowed by voucher types.
  const udfSelectOptions = (scopeIds = [], numericOnly = false) => {
    const selectedNames = new Set(
      (options.voucherTypes || []).filter((v) => scopeIds.includes(v._id)).map((v) => v.Name)
    );
    return (options.udfFields || [])
      .filter((u) => (numericOnly ? u.numeric : true))
      .filter((u) => !scopeIds.length || u.voucherTypes.some((t) => selectedNames.has(t)))
      .map((u) => ({ value: `${u.name}||${u.subField || ''}`, label: u.label }));
  };

  const parseUdf = (encoded) => {
    const [name, subField] = String(encoded || '').split('||');
    return { udfName: name || '', udfSubField: subField || '' };
  };

  const updateReport = (idx, patch) =>
    update({ reports: reports.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const addReport = () =>
    update({
      reports: [
        ...reports,
        {
          key: uniqueKey('report', reports.map((r) => r.key)),
          label: '',
          enabled: true,
          groupBy: [],
          measures: [
            {
              key: 'measure',
              label: '',
              format: 'currency',
              type: 'sumVoucherField',
              voucherTypes: [],
              field: 'totalAmount',
              ledgerName: '',
              ledgerGroupName: 'Sundry Debtors',
              udfName: '',
              udfSubField: '',
              formula: [],
            },
          ],
          conditions: [],
          breakdownMode: 'period',
          periodMode: 'month',
          breakdownMeasureKey: '',
          alerts: { debtorDaysLimit: 0, debtorDaysMeasureKey: '' },
        },
      ],
    });
  const removeReport = (idx) => update({ reports: reports.filter((_, i) => i !== idx) });
  const moveReport = (idx, dir) => {
    const next = [...reports];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    update({ reports: next });
  };

  const addMeasure = (rIdx) => {
    const r = reports[rIdx];
    const key = uniqueKey('measure', (r.measures || []).map((m) => m.key));
    updateReport(rIdx, {
      measures: [
        ...(r.measures || []),
        {
          key,
          label: '',
          format: 'currency',
          type: 'sumVoucherField',
          voucherTypes: [],
          field: 'totalAmount',
          ledgerName: '',
          ledgerGroupName: 'Sundry Debtors',
          udfName: '',
          udfSubField: '',
          formula: [],
        },
      ],
    });
  };
  const updateMeasure = (rIdx, mIdx, patch) =>
    updateReport(rIdx, {
      measures: (reports[rIdx].measures || []).map((m, i) => (i === mIdx ? { ...m, ...patch } : m)),
    });
  const removeMeasure = (rIdx, mIdx) =>
    updateReport(rIdx, { measures: (reports[rIdx].measures || []).filter((_, i) => i !== mIdx) });
  const moveMeasure = (rIdx, mIdx, dir) => {
    const next = [...(reports[rIdx].measures || [])];
    const t = mIdx + dir;
    if (t < 0 || t >= next.length) return;
    [next[mIdx], next[t]] = [next[t], next[mIdx]];
    updateReport(rIdx, { measures: next });
  };

  const updateFormula = (rIdx, mIdx, sIdx, patch) => {
    const m = reports[rIdx].measures[mIdx];
    updateMeasure(rIdx, mIdx, {
      formula: (m.formula || []).map((s, i) => (i === sIdx ? { ...s, ...patch } : s)),
    });
  };
  const addFormulaStep = (rIdx, mIdx) => {
    const m = reports[rIdx].measures[mIdx];
    updateMeasure(rIdx, mIdx, { formula: [...(m.formula || []), { operator: '+', measureKey: '', constant: 0 }] });
  };
  const removeFormulaStep = (rIdx, mIdx, sIdx) => {
    const m = reports[rIdx].measures[mIdx];
    updateMeasure(rIdx, mIdx, { formula: (m.formula || []).filter((_, i) => i !== sIdx) });
  };

  const addDimension = (rIdx) => {
    const r = reports[rIdx];
    const key = uniqueKey('dim', (r.groupBy || []).map((d) => d.key));
    updateReport(rIdx, {
      groupBy: [
        ...(r.groupBy || []),
        { key, label: '', type: 'voucherType', udfName: '', udfSubField: '', udfScope: 'all', udfVoucherTypes: [] },
      ],
    });
  };
  const updateDimension = (rIdx, dIdx, patch) =>
    updateReport(rIdx, {
      groupBy: (reports[rIdx].groupBy || []).map((d, i) => (i === dIdx ? { ...d, ...patch } : d)),
    });
  const removeDimension = (rIdx, dIdx) =>
    updateReport(rIdx, { groupBy: (reports[rIdx].groupBy || []).filter((_, i) => i !== dIdx) });

  const addCondition = (rIdx) =>
    updateReport(rIdx, {
      conditions: [
        ...(reports[rIdx].conditions || []),
        { field: 'voucherTypeName', operator: 'eq', value: '', udfName: '', udfSubField: '' },
      ],
    });
  const updateCondition = (rIdx, cIdx, patch) =>
    updateReport(rIdx, {
      conditions: (reports[rIdx].conditions || []).map((c, i) => (i === cIdx ? { ...c, ...patch } : c)),
    });
  const removeCondition = (rIdx, cIdx) =>
    updateReport(rIdx, { conditions: (reports[rIdx].conditions || []).filter((_, i) => i !== cIdx) });

  const inputCls = 'p-2 border rounded text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';
  const darkText = 'text-gray-500 dark:text-gray-400';
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const selectStyles = isDark
    ? {
        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563' }),
        menu: (base) => ({ ...base, backgroundColor: '#374151', zIndex: 50 }),
        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : 'transparent', color: '#E5E7EB' }),
        multiValue: (base) => ({ ...base, backgroundColor: '#4B5563' }),
        multiValueLabel: (base) => ({ ...base, color: '#E5E7EB' }),
        singleValue: (base) => ({ ...base, color: '#E5E7EB' }),
        input: (base) => ({ ...base, color: '#E5E7EB' }),
        placeholder: (base) => ({ ...base, color: '#9CA3AF' }),
        menuList: (base) => ({ ...base, maxHeight: 240 }),
      }
    : {};

  return (
    <div className="col-span-4 border p-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 rounded mt-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Tally Reports</h3>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
            className="rounded border-gray-300"
          />
          Enable Tally Reports
        </label>
      </div>

      {optionsError && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 rounded text-xs">{optionsError}</div>
      )}
      {!optionsError && companyId && (options.voucherTypes || []).length === 0 && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300 rounded text-xs">
          No Tally voucher types found for this company. Sync voucher types from Tally first.
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Reports</h4>
        <button type="button" onClick={addReport} className="text-xs px-2 py-1 bg-blue-600 text-white rounded">
          + Add Report
        </button>
      </div>
      {reports.length === 0 && <p className="text-xs text-gray-500 dark:text-gray-400">No reports configured.</p>}

      {reports.map((report, rIdx) => (
        <div key={report.key || rIdx} className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded p-3 mb-3">
          {/* header */}
          <div className="grid grid-cols-12 gap-2 items-center mb-3">
            <input
              type="text"
              value={report.label || ''}
              onChange={(e) => updateReport(rIdx, { label: e.target.value })}
              placeholder="Report name (e.g. Sales Summary)"
              className={`col-span-4 ${inputCls}`}
            />
            <select
              value={report.type || 'voucher'}
              onChange={(e) => updateReport(rIdx, { type: e.target.value })}
              className={`col-span-3 ${inputCls}`}
            >
              <option value="voucher">Voucher Summary</option>
              <option value="debtor">Debtor Outstanding</option>
            </select>
            <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={report.enabled !== false}
                onChange={(e) => updateReport(rIdx, { enabled: e.target.checked })}
              />
              Enabled
            </label>
            <div className="col-span-3 flex justify-end gap-1">
              <button type="button" onClick={() => moveReport(rIdx, -1)} className="px-2 py-1 border rounded text-xs">↑</button>
              <button type="button" onClick={() => moveReport(rIdx, 1)} className="px-2 py-1 border rounded text-xs">↓</button>
              <button type="button" onClick={() => removeReport(rIdx)} className="px-2 py-1 bg-red-500 text-white rounded text-xs">
                Remove Report
              </button>
             </div>
           </div>

          {report.type !== 'debtor' && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Period</label>
              <select
                value={report.periodMode || 'month'}
                onChange={(e) => updateReport(rIdx, { periodMode: e.target.value })}
                className={`w-40 ${inputCls}`}
              >
                <option value="month">Month</option>
                <option value="dateRange">Date range</option>
              </select>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Breakdown</label>
              <select
                value={report.breakdownMode || 'period'}
                onChange={(e) => updateReport(rIdx, { breakdownMode: e.target.value })}
                className={`w-52 ${inputCls}`}
              >
                <option value="period">Period columns</option>
                <option value="matrix">Measures matrix</option>
              </select>
              {report.breakdownMode === 'matrix' && (
                <>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Percent of</label>
                  <select
                    value={report.matrixPercentKey || ''}
                    onChange={(e) => updateReport(rIdx, { matrixPercentKey: e.target.value })}
                    className={`w-52 ${inputCls}`}
                  >
                    <option value="">None</option>
                    {(report.measures || []).map((m) => (
                      <option key={m.key} value={m.key}>{m.label || m.key}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}

          {report.type === 'debtor' ? (
            <div className="border dark:border-gray-700 rounded p-2 space-y-2">
              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-3 text-xs font-medium text-gray-600 dark:text-gray-400">Debtor groups</label>
                <input
                  type="text"
                  value={(report.debtor?.groups || []).join(', ')}
                  onChange={(e) =>
                    updateReport(rIdx, {
                      debtor: {
                        ...(report.debtor || {}),
                        groups: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      },
                    })
                  }
                  placeholder="blank = all groups containing 'Debtor'"
                  className={`col-span-9 ${inputCls}`}
                />
              </div>

              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-3 text-xs font-medium text-gray-600 dark:text-gray-400">Date range</label>
                <select
                  value={report.debtor?.dateMode || 'currentFY'}
                  onChange={(e) =>
                    updateReport(rIdx, { debtor: { ...(report.debtor || {}), dateMode: e.target.value } })
                  }
                  className={`col-span-3 ${inputCls}`}
                >
                  <option value="currentFY">Current FY</option>
                  <option value="last12">Last 12 months</option>
                  <option value="custom">Custom</option>
                  <option value="all">All</option>
                </select>
                {report.debtor?.dateMode === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={report.debtor?.dateFrom ? String(report.debtor.dateFrom).slice(0, 10) : ''}
                      onChange={(e) =>
                        updateReport(rIdx, { debtor: { ...(report.debtor || {}), dateFrom: e.target.value } })
                      }
                      className={`col-span-3 ${inputCls}`}
                    />
                    <input
                      type="date"
                      value={report.debtor?.dateTo ? String(report.debtor.dateTo).slice(0, 10) : ''}
                      onChange={(e) =>
                        updateReport(rIdx, { debtor: { ...(report.debtor || {}), dateTo: e.target.value } })
                      }
                      className={`col-span-3 ${inputCls}`}
                    />
                  </>
                )}
              </div>

              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={!!report.debtor?.includeZero}
                  onChange={(e) =>
                    updateReport(rIdx, { debtor: { ...(report.debtor || {}), includeZero: e.target.checked } })
                  }
                />
                Include zero-balance debtors
              </label>

              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-3 text-xs font-medium text-gray-600 dark:text-gray-400">Invoice types</label>
                <input
                  type="text"
                  value={(report.debtor?.invoiceVoucherTypes || []).join(', ')}
                  onChange={(e) =>
                    updateReport(rIdx, {
                      debtor: {
                        ...(report.debtor || {}),
                        invoiceVoucherTypes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      },
                    })
                  }
                  placeholder="blank = voucher types under Sales"
                  className={`col-span-9 ${inputCls}`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                    Age buckets (from due date)
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      updateReport(rIdx, {
                        debtor: {
                          ...(report.debtor || {}),
                          ageBuckets: [...(report.debtor?.ageBuckets || []), { label: '', from: 0, to: null }],
                        },
                      })
                    }
                    className="text-xs px-2 py-0.5 border rounded"
                  >
                    + Bucket
                  </button>
                </div>
                {(report.debtor?.ageBuckets || []).map((b, bIdx) => (
                  <div key={bIdx} className="grid grid-cols-12 gap-2 items-center mb-1">
                    <input
                      type="text"
                      value={b.label || ''}
                      onChange={(e) => {
                        const next = (report.debtor?.ageBuckets || []).map((x, i) =>
                          i === bIdx ? { ...x, label: e.target.value } : x
                        );
                        updateReport(rIdx, { debtor: { ...(report.debtor || {}), ageBuckets: next } });
                      }}
                      placeholder="Label"
                      className={`col-span-4 ${inputCls}`}
                    />
                    <input
                      type="number"
                      value={b.from ?? 0}
                      onChange={(e) => {
                        const next = (report.debtor?.ageBuckets || []).map((x, i) =>
                          i === bIdx ? { ...x, from: Number(e.target.value) } : x
                        );
                        updateReport(rIdx, { debtor: { ...(report.debtor || {}), ageBuckets: next } });
                      }}
                      placeholder="From"
                      className={`col-span-3 ${inputCls}`}
                    />
                    <input
                      type="number"
                      value={b.to ?? ''}
                      onChange={(e) => {
                        const next = (report.debtor?.ageBuckets || []).map((x, i) =>
                          i === bIdx ? { ...x, to: e.target.value === '' ? null : Number(e.target.value) } : x
                        );
                        updateReport(rIdx, { debtor: { ...(report.debtor || {}), ageBuckets: next } });
                      }}
                      placeholder="To (blank = ∞)"
                      className={`col-span-3 ${inputCls}`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        updateReport(rIdx, {
                          debtor: {
                            ...(report.debtor || {}),
                            ageBuckets: (report.debtor?.ageBuckets || []).filter((_, i) => i !== bIdx),
                          },
                        })
                      }
                      className="col-span-2 px-2 py-1 bg-red-500 text-white rounded text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {(report.debtor?.ageBuckets || []).length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Default buckets: 0-30 / 31-60 / 61-90 / &gt;90
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
          {/* dimensions */}
          <div className="border dark:border-gray-700 rounded p-2 mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Group By (optional)</span>
              <button type="button" onClick={() => addDimension(rIdx)} className="text-xs px-2 py-0.5 border rounded">
                + Dimension
              </button>
            </div>
            {(report.groupBy || []).map((dim, dIdx) => {
              const udfInsts = udfSelectOptions(
                dim.udfScope === 'voucherTypes' ? dim.udfVoucherTypes || [] : [],
                false
              );
              return (
                <div key={dim.key || dIdx} className="grid grid-cols-12 gap-2 items-center mb-1">
                  <select
                    value={dim.type || 'voucherType'}
                    onChange={(e) => updateDimension(rIdx, dIdx, { type: e.target.value })}
                    className={`col-span-2 ${inputCls}`}
                  >
                    <option value="voucherType">Voucher Type</option>
                    <option value="udf">UDF</option>
                    <option value="salesLedger">Sales Ledger</option>
                  </select>
                  <input
                    type="text"
                    value={dim.label || ''}
                    onChange={(e) => updateDimension(rIdx, dIdx, { label: e.target.value })}
                    placeholder="Column label"
                    className={`col-span-2 ${inputCls}`}
                  />
                  {dim.type === 'udf' ? (
                    <>
                      <select
                        value={dim.udfScope || 'all'}
                        onChange={(e) => updateDimension(rIdx, dIdx, { udfScope: e.target.value })}
                        className={`col-span-3 ${inputCls}`}
                      >
                        <option value="all">All vouchers</option>
                        <option value="voucherTypes">Specific voucher types</option>
                      </select>
                      {dim.udfScope === 'voucherTypes' && (
                        <Select
                      styles={selectStyles}
                          isMulti
                          options={vtOptions}
                          value={vtOptions.filter((o) => (dim.udfVoucherTypes || []).includes(o.value))}
                          onChange={(sel) => updateDimension(rIdx, dIdx, { udfVoucherTypes: (sel || []).map((s) => s.value) })}
                          placeholder="Voucher types"
                          className="col-span-4 text-sm"
                        />
                      )}
                      <Select
                      styles={selectStyles}
                        options={udfInsts}
                        value={udfInsts.find((o) => o.value === `${dim.udfName}||${dim.udfSubField || ''}`) || null}
                        onChange={(sel) => updateDimension(rIdx, dIdx, parseUdf(sel ? sel.value : ''))}
                        placeholder="UDF field"
                        isClearable
                        className={`${dim.udfScope === 'voucherTypes' ? 'col-span-1' : 'col-span-5'} text-sm`}
                      />
                    </>
                  ) : (
                    <span className="col-span-6" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeDimension(rIdx, dIdx)}
                    className="col-span-1 px-2 py-1 bg-red-500 text-white rounded text-xs"
                  >
                    x
                  </button>

                  {dim.type === 'salesLedger' && (
                    <div className="col-span-12 ml-2 border-l-2 border-gray-200 dark:border-gray-700 pl-2 pt-1 space-y-1">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <label className="col-span-2 text-xs font-medium text-gray-600 dark:text-gray-400">Fallback UDF</label>
                        <Select
                          styles={selectStyles}
                          options={udfInsts}
                          value={udfInsts.find((o) => o.value === `${dim.udfName}||${dim.udfSubField || ''}`) || null}
                          onChange={(sel) => updateDimension(rIdx, dIdx, parseUdf(sel ? sel.value : ''))}
                          placeholder="UDF fallback"
                          isClearable
                          className="col-span-4 text-sm"
                        />
                        <input
                          type="text"
                          value={dim.emptyLabel || ''}
                          onChange={(e) => updateDimension(rIdx, dIdx, { emptyLabel: e.target.value })}
                          placeholder="Empty label (e.g. OTHERS)"
                          className={`col-span-3 ${inputCls}`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            updateDimension(rIdx, dIdx, {
                              ledgerMap: [...(dim.ledgerMap || []), { match: '', matchMode: 'contains', label: '' }],
                            })
                          }
                          className="col-span-3 text-xs px-2 py-1 border rounded"
                        >
                          + Ledger rule
                        </button>
                      </div>
                      {(dim.ledgerMap || []).map((rule, lIdx) => (
                        <div key={lIdx} className="grid grid-cols-12 gap-2 items-center">
                          <input
                            type="text"
                            value={rule.match || ''}
                            onChange={(e) =>
                              updateDimension(rIdx, dIdx, {
                                ledgerMap: (dim.ledgerMap || []).map((r, i) =>
                                  i === lIdx ? { ...r, match: e.target.value } : r
                                ),
                              })
                            }
                            placeholder="Ledger name contains"
                            className={`col-span-5 ${inputCls}`}
                          />
                          <select
                            value={rule.matchMode || 'contains'}
                            onChange={(e) =>
                              updateDimension(rIdx, dIdx, {
                                ledgerMap: (dim.ledgerMap || []).map((r, i) =>
                                  i === lIdx ? { ...r, matchMode: e.target.value } : r
                                ),
                              })
                            }
                            className={`col-span-2 ${inputCls}`}
                          >
                            <option value="contains">contains</option>
                            <option value="exact">exact</option>
                          </select>
                          <input
                            type="text"
                            value={rule.label || ''}
                            onChange={(e) =>
                              updateDimension(rIdx, dIdx, {
                                ledgerMap: (dim.ledgerMap || []).map((r, i) =>
                                  i === lIdx ? { ...r, label: e.target.value } : r
                                ),
                              })
                            }
                            placeholder="Label (e.g. RLU-2 SALES)"
                            className={`col-span-4 ${inputCls}`}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              updateDimension(rIdx, dIdx, {
                                ledgerMap: (dim.ledgerMap || []).filter((_, i) => i !== lIdx),
                              })
                            }
                            className="col-span-1 px-2 py-1 bg-red-500 text-white rounded text-xs"
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* measures */}
          <div className="border dark:border-gray-700 rounded p-2 mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Measures / KPIs</span>
              <button type="button" onClick={() => addMeasure(rIdx)} className="text-xs px-2 py-0.5 border rounded">
                + Measure
              </button>
            </div>
            {(report.measures || []).map((m, mIdx) => {
              const numericUdfs = udfSelectOptions(m.voucherTypes || [], true);
              return (
                <div key={m.key || mIdx} className="border dark:border-gray-700 rounded p-2 mb-2 bg-gray-50 dark:bg-gray-800">
                  <div className="grid grid-cols-12 gap-2 items-center mb-1">
                    <input
                      type="text"
                      value={m.label || ''}
                      onChange={(e) => updateMeasure(rIdx, mIdx, { label: e.target.value })}
                      placeholder="Measure label (e.g. Gross Sales)"
                      className={`col-span-3 ${inputCls}`}
                    />
                    <select
                      value={m.type || 'sumVoucherField'}
                      onChange={(e) => updateMeasure(rIdx, mIdx, { type: e.target.value })}
                      className={`col-span-3 ${inputCls}`}
                    >
                      {MEASURE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <select
                      value={m.format || 'currency'}
                      onChange={(e) => updateMeasure(rIdx, mIdx, { format: e.target.value })}
                      className={`col-span-2 ${inputCls}`}
                    >
                      {KPI_FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <div className="col-span-4 flex items-center justify-end gap-2">
                      <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400" title="Compute but don't show this row">
                        <input
                          type="checkbox"
                          checked={!!m.hidden}
                          onChange={(e) => updateMeasure(rIdx, mIdx, { hidden: e.target.checked })}
                        />
                        Hide
                      </label>
                      <button type="button" onClick={() => moveMeasure(rIdx, mIdx, -1)} className="px-2 py-1 border rounded text-xs">↑</button>
                      <button type="button" onClick={() => moveMeasure(rIdx, mIdx, 1)} className="px-2 py-1 border rounded text-xs">↓</button>
                      <button type="button" onClick={() => removeMeasure(rIdx, mIdx)} className="px-2 py-1 bg-red-500 text-white rounded text-xs">
                        Remove
                      </button>
                    </div>
                  </div>

                  {m.type !== 'derived' && (
                    <div className="grid grid-cols-12 gap-2 items-center mb-1">
                      <Select
                      styles={selectStyles}
                        isMulti
                        options={vtOptions}
                        value={vtOptions.filter((o) => (m.voucherTypes || []).includes(o.value))}
                        onChange={(sel) => updateMeasure(rIdx, mIdx, { voucherTypes: (sel || []).map((s) => s.value) })}
                        placeholder="Voucher types (empty = all)"
                        className="col-span-6 text-sm"
                      />
                      {m.type === 'sumVoucherField' && (
                        <select
                          value={m.field || 'totalAmount'}
                          onChange={(e) => updateMeasure(rIdx, mIdx, { field: e.target.value })}
                          className={`col-span-3 ${inputCls}`}
                        >
                          <option value="totalAmount">Total Amount</option>
                          <option value="totalAmountBeforeTax">Before Tax</option>
                        </select>
                      )}
                      {m.type === 'sumLedgerAmount' && (
                        <>
                          <select
                            value={m.ledgerMatch || 'exact'}
                            onChange={(e) => updateMeasure(rIdx, mIdx, { ledgerMatch: e.target.value })}
                            className={`col-span-1 ${inputCls}`}
                          >
                            <option value="exact">Exact</option>
                            <option value="contains">Contains</option>
                          </select>
                          <input
                            type="text"
                            value={
                              (m.ledgerNames && m.ledgerNames.length
                                ? m.ledgerNames
                                : m.ledgerName
                                  ? [m.ledgerName]
                                  : []
                              ).join(', ')
                            }
                            onChange={(e) =>
                              updateMeasure(rIdx, mIdx, {
                                ledgerNames: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                              })
                            }
                            placeholder="Ledger names (comma separated)"
                            className={`col-span-2 ${inputCls}`}
                          />
                        </>
                      )}
                      {m.type === 'sumItemAmount' && (
                        <span className={`col-span-3 text-xs ${darkText}`}>
                          Sums item line amounts (excludes GST, freight, round-off).
                        </span>
                      )}
                      {m.type === 'sumLedgerBalance' && (
                        <input
                          type="text"
                          value={m.ledgerGroupName || ''}
                          onChange={(e) => updateMeasure(rIdx, mIdx, { ledgerGroupName: e.target.value })}
                          placeholder="Ledger group (e.g. Sundry Debtors)"
                          className={`col-span-3 ${inputCls}`}
                        />
                      )}
                      {m.type === 'sumUdf' && (
                        <>
                          <Select
                      styles={selectStyles}
                            options={numericUdfs}
                            value={numericUdfs.find((o) => o.value === `${m.udfName}||${m.udfSubField || ''}`) || null}
                            onChange={(sel) => updateMeasure(rIdx, mIdx, parseUdf(sel ? sel.value : ''))}
                            placeholder="Numeric UDF"
                            isClearable
                            className="col-span-3 text-sm"
                          />
                          {numericUdfs.length === 0 && (
                            <span className="col-span-3 text-xs text-red-600 dark:text-red-400">
                              No numeric UDF available for this selection.
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {m.type === 'derived' && (
                    <div className="space-y-1">
                      {(m.formula || []).map((step, sIdx) => (
                        <div key={sIdx} className="grid grid-cols-12 gap-2 items-center">
                          {sIdx === 0 ? (
                            <span className="col-span-2 text-xs text-gray-500 dark:text-gray-400">Start</span>
                          ) : (
                            <select
                              value={step.operator || '+'}
                              onChange={(e) => updateFormula(rIdx, mIdx, sIdx, { operator: e.target.value })}
                              className={`col-span-2 ${inputCls}`}
                            >
                              {FORMULA_OPS.map((op) => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </select>
                          )}
                          {sIdx !== 0 && step.operator === '×' ? (
                            <input
                              type="number"
                              value={step.constant ?? 0}
                              onChange={(e) => updateFormula(rIdx, mIdx, sIdx, { constant: Number(e.target.value) })}
                              className={`col-span-7 ${inputCls}`}
                            />
                          ) : (
                            <select
                              value={step.measureKey || ''}
                              onChange={(e) => updateFormula(rIdx, mIdx, sIdx, { measureKey: e.target.value })}
                              className={`col-span-7 ${inputCls}`}
                            >
                              <option value="">Select measure</option>
                              {(report.measures || [])
                                .filter((x, xIdx) => x.key !== m.key && (x.type !== 'derived' || xIdx < mIdx))
                                .map((x) => (
                                  <option key={x.key} value={x.key}>{x.label || x.key}</option>
                                ))}
                            </select>
                          )}
                          {sIdx === 0 ? (
                            <span className="col-span-3" />
                          ) : (
                            <button type="button" onClick={() => removeFormulaStep(rIdx, mIdx, sIdx)} className="col-span-3 px-2 py-1 border rounded text-xs">
                              Remove step
                            </button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={() => addFormulaStep(rIdx, mIdx)} className="text-xs px-2 py-1 border rounded">
                        + Step
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* conditions */}
          <div className="border dark:border-gray-700 rounded p-2 mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Conditions (all must match)</span>
              <button type="button" onClick={() => addCondition(rIdx)} className="text-xs px-2 py-0.5 border rounded">
                + Condition
              </button>
            </div>
            {(report.conditions || []).map((c, cIdx) => {
              const anyUdfs = udfSelectOptions([], false);
              return (
                <div key={cIdx} className="grid grid-cols-12 gap-2 items-center mb-1">
                  <select
                    value={c.field || 'voucherTypeName'}
                    onChange={(e) => updateCondition(rIdx, cIdx, { field: e.target.value })}
                    className={`col-span-3 ${inputCls}`}
                  >
                    {CONDITION_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  {c.field === 'udf' && (
                    <Select
                      styles={selectStyles}
                      options={anyUdfs}
                      value={anyUdfs.find((o) => o.value === `${c.udfName}||${c.udfSubField || ''}`) || null}
                      onChange={(sel) => updateCondition(rIdx, cIdx, parseUdf(sel ? sel.value : ''))}
                      placeholder="UDF field"
                      isClearable
                      className="col-span-3 text-sm"
                    />
                  )}
                  <select
                    value={c.operator || 'eq'}
                    onChange={(e) => updateCondition(rIdx, cIdx, { operator: e.target.value })}
                    className={`${c.field === 'udf' ? 'col-span-2' : 'col-span-3'} ${inputCls}`}
                  >
                    {CONDITION_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  {c.field === 'isCancelled' ? (
                    <select
                      value={c.value === 'true' ? 'true' : 'false'}
                      onChange={(e) => updateCondition(rIdx, cIdx, { value: e.target.value })}
                      className={`${c.field === 'udf' ? 'col-span-3' : 'col-span-5'} ${inputCls}`}
                    >
                      <option value="false">false (not cancelled)</option>
                      <option value="true">true (cancelled)</option>
                    </select>
                  ) : c.field === 'voucherStatus' ? (
                    <select
                      value={c.value || ''}
                      onChange={(e) => updateCondition(rIdx, cIdx, { value: e.target.value })}
                      className={`${c.field === 'udf' ? 'col-span-3' : 'col-span-5'} ${inputCls}`}
                    >
                      <option value="">Select status</option>
                      <option value="Pending">Pending</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Completed">Completed</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={c.value || ''}
                      onChange={(e) => updateCondition(rIdx, cIdx, { value: e.target.value })}
                      placeholder="Value"
                      className={`${c.field === 'udf' ? 'col-span-3' : 'col-span-5'} ${inputCls}`}
                    />
                  )}
                  {c.field === 'udf' && <span className="col-span-1" />}
                  <button
                    type="button"
                    onClick={() => removeCondition(rIdx, cIdx)}
                    className="col-span-1 px-2 py-1 bg-red-500 text-white rounded text-xs"
                  >
                    x
                  </button>
                </div>
              );
            })}
          </div>

          {/* breakdown + alerts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Breakdown Measure</label>
              <select
                value={report.breakdownMeasureKey || ''}
                onChange={(e) => updateReport(rIdx, { breakdownMeasureKey: e.target.value })}
                className={`w-full ${inputCls}`}
              >
                <option value="">First measure</option>
                {(report.measures || []).map((m) => (
                  <option key={m.key} value={m.key}>{m.label || m.key}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Debtor Days Limit</label>
              <input
                type="number"
                value={(report.alerts && report.alerts.debtorDaysLimit) || 0}
                onChange={(e) =>
                  updateReport(rIdx, {
                    alerts: { ...(report.alerts || {}), debtorDaysLimit: Number(e.target.value) },
                  })
                }
                className={`w-full ${inputCls}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Debtor Days Measure</label>
              <select
                value={(report.alerts && report.alerts.debtorDaysMeasureKey) || ''}
                onChange={(e) =>
                  updateReport(rIdx, {
                    alerts: { ...(report.alerts || {}), debtorDaysMeasureKey: e.target.value },
                  })
                }
                className={`w-full ${inputCls}`}
              >
                <option value="">None</option>
                {(report.measures || []).map((m) => (
                  <option key={m.key} value={m.key}>{m.label || m.key}</option>
                ))}
              </select>
            </div>
          </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// Reports the employee Management Reports page can render. Keys must match the
// access keys used on the company head dashboard reports.
const MANAGEMENT_REPORT_CATALOG = [
  { key: 'management-dashboard', label: 'Management Dashboard' },
  { key: 'delivery-orders-reports', label: 'Delivery Orders' },
  { key: 'delivery-orders-detailed-reports', label: 'Delivery Orders (Detailed)' },
  { key: 'quotations-reports', label: 'Quotations' },
  { key: 'proforma-invoice-reports', label: 'Proforma Invoices' },
  { key: 'choice-entry-reports', label: 'Choice Entry' },
  { key: 'goods-return-reports', label: 'Goods Return' },
  { key: 'tally-reports', label: 'Tally Reports' },
];

// Leave empty by default: the employee page then follows the company head
// dashboard's selected report features until an explicit selection is saved.
const DEFAULT_MANAGEMENT_REPORT_SETTINGS = { enabled: false, reports: [] };

const ManagementReportSettingsManager = ({ value, onChange }) => {
  const settings = value || DEFAULT_MANAGEMENT_REPORT_SETTINGS;
  const reports = settings.reports || [];
  const update = (patch) => onChange({ ...settings, ...patch });

  const isSelected = (key) => reports.some((r) => r.key === key);
  const toggle = (item) => {
    if (isSelected(item.key)) {
      update({ reports: reports.filter((r) => r.key !== item.key) });
      return;
    }
    const entry = { key: item.key, label: item.label, enabled: true, order: 0 };
    const next =
      item.key === 'management-dashboard' ? [entry, ...reports] : [...reports, entry];
    update({ enabled: true, reports: next.map((r, i) => ({ ...r, order: i })) });
  };
  const updateReport = (key, patch) =>
    update({ reports: reports.map((r) => (r.key === key ? { ...r, ...patch } : r)) });
  const move = (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= reports.length) return;
    const next = [...reports];
    [next[idx], next[t]] = [next[t], next[idx]];
    update({ reports: next.map((r, i) => ({ ...r, order: i })) });
  };

  return (
    <div className="col-span-4 border p-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 rounded mt-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Management Reports</h3>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Select which company reports employees with the Management Reports access can view.
        If none are selected, employees see the reports enabled on the company head dashboard.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        {MANAGEMENT_REPORT_CATALOG.map((item) => (
          <label key={item.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded px-2 py-1 cursor-pointer">
            <input
              type="checkbox"
              checked={isSelected(item.key)}
              onChange={() => toggle(item)}
              className="rounded border-gray-300"
            />
            {item.label}
          </label>
        ))}
      </div>

      {reports.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No reports selected.</p>
      ) : (
        <div className="space-y-1">
          {reports.map((r, idx) => (
            <div key={r.key} className="flex items-center gap-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded px-2 py-1">
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">
                {MANAGEMENT_REPORT_CATALOG.find((c) => c.key === r.key)?.label || r.label || r.key}
              </span>
              <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={r.enabled !== false}
                  onChange={(e) => updateReport(r.key, { enabled: e.target.checked })}
                />
                Enabled
              </label>
              <button type="button" onClick={() => move(idx, -1)} className="px-2 py-0.5 border rounded text-xs">↑</button>
              <button type="button" onClick={() => move(idx, 1)} className="px-2 py-0.5 border rounded text-xs">↓</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editCompany, setEditCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'form'
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCompany, setViewCompany] = useState(null);
  const [tallyCompanies, setTallyCompanies] = useState([]);
  const [selectedTallyName, setSelectedTallyName] = useState('');
  const [selectedTallyGUID, setSelectedTallyGUID] = useState('');
  const [currentActiveCompany, setCurrentActiveCompany] = useState('');
  const [loadingTally, setLoadingTally] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [domains, setDomains] = useState([
    {
      domainName: '',
      adminDashboardAccess: [],
      companyHeadDashboardAccess: [],
      employeeDashboardAccess: [],
    },
  ]);

  const [selectedDealerRefs, setSelectedDealerRefs] = useState([]);
  const [linkedDealers, setLinkedDealers] = useState([]);
  const [allDealers, setAllDealers] = useState([]);
  const [quotationTermsAndConditions, setQuotationTermsAndConditions] = useState([]);
  const [deliveryOrderTermsAndConditions, setDeliveryOrderTermsAndConditions] = useState([]);
  const [selectedAdditionalTallyAccess, setSelectedAdditionalTallyAccess] = useState([]);
  const [usageVoucherTypes, setUsageVoucherTypes] = useState([]);
  const [tallyItemsFieldSettings, setTallyItemsFieldSettings] = useState({});
  const [tallyManagementAccess, setTallyManagementAccess] = useState({
    allowParameterAssignment: true,
    allowBrandAssignment: true,
    allowImageUpload: true,
  });
  const [reorderStatusFieldSettings, setReorderStatusFieldSettings] = useState({});
  const [includeCancelledInCalculations, setIncludeCancelledInCalculations] = useState(false);
  const [tallyReportSettings, setTallyReportSettings] = useState(DEFAULT_TALLY_REPORT_SETTINGS);
  const [managementReportSettings, setManagementReportSettings] = useState(DEFAULT_MANAGEMENT_REPORT_SETTINGS);

  const createFormRef = useRef(null);
  const editFormRef = useRef(null);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
  });

  // Organized access permissions by categories
  const defaultAdminAccess = [
    // Core Operations
    'delivery-order',
    'items',
    'quotation',
    'crm',
    
    // Tally Integration
    'tally-quotation',
    'tally-items',
    
    // Management & Reports
    'activity-log',
    'campaign-management',
    'schemes-reports',
    'tally-reports',
  ];

  const defaultCompanyHeadAccess = [
    // Dashboard & Core
    'dashboard',
    
    // HR Management
    'employee-management',
    'leave-management',
    'attendance-management',
    'payroll-management',
    'role-management',
    
    // Asset & Brand Management
    'asset-management',
    'brand-management',
    
    // System Management
    'tally-management',
    
    // App Dealer Management
    'app-dealer-management',
    'app-dealer-access-logs',
    'app-dealer-interest-reports',
    
    // Reports & Analytics
    'delivery-orders-reports',
    'activity-logs-reports',
    'choice-entry-reports',
    'quotations-reports', 
    'proforma-invoice-reports',
    'delivery-orders-detailed-reports',
    'goods-return-reports',
    'showroom-analytics',
    'tally-reports',
    
    // Financial
    'tiles-day-book',
    'tiles-ledger',
    
    // Tiles Management
    'tiles-management',
    
    // Stock Items Management
    'stock-items-management',
    
    // Automations
    'whatsapp-automations',
  ];

  const defaultEmployeeAccess = [
    // Dashboard & Core
    'dashboard',
    
    // Core Operations
    'delivery-order',
    'quotation',
    'quotation-new',
    'estimate',
    'proforma-invoice',
    'packing-slip',
    'goods-received',
    
    // Customer & Sales Management
    'tele-calling-payment',
    'customer-management',
    'dealer-management',
    'buyers',
    'supplier-management',
    'guest-master',
    'visitor-management',
    'reference-management',
    'ma-management',
    
    // Inventory & Items
    'item-management',
    'purchase-indent',
    'reference-commission',
    
    // HR & Personal
    'apply-leave',
    'attendance',
    'field-visit',
    'reminders',
    
    // Activity & Logs
    'activity-log',
    'activity-logs-new',
    'choice-entry',
    
    // Reports & Analytics
    'management-reports',
    
    // Location & Transport
    'station-management',
    'transport-management',
    
    // Doors Management
    'doors',
    'doors-inward',
    'doors-outward',
    'doors-stock-status',
    'doors-parameters',
    
    // Tiles Management
    'tiles-master',
    'tiles-parameters',
    'tiles-stock-status',
    'tiles-inward',
    'tiles-outward',
    'tiles-delivery-order',
    'tiles-sales-order',
    'tiles-location-transfer',
    'tiles-day-book',
    'tiles-ledger',
    'tiles-order',
    'tiles-dispatch-entry',
    'tiles-received',
    
    // Tally Integration
    'tally-quotation',
    'tally-items-management',
    'tally-parameters-management',
    'tally-ledger-management',
    
    // Stock Management
    'stock-master',
    'stock-items-inward',
    'stock-items-outward',
    'stock-items-location-transfer',
    'stock-items-status',
    'stock-items-ledger',
    'stock-items-day-book',
    'stock-movement',
    'stock-items-movement',
    'stock-items-order',
    'stock-items-dispatch',
    'stock-items-received',
    'stock-items-destroy',
    
    // Product & Inventory
    'concept-product',
    'demolished-tiles',
    'tiles-return',
    'goods-return',
    'tally-operations-panel',
    'display-panel-management',
    'pdf-catalogs',
  ];

  useEffect(() => {
    if (editCompany) {
      setQuotationTermsAndConditions(editCompany.quotationTermsAndConditions || []);
      setDeliveryOrderTermsAndConditions(editCompany.deliveryOrderTermsAndConditions || []);
      setUsageVoucherTypes(
        (editCompany.usageVoucherTypes || [])
          .map(cfg => {
            const vt = cfg && typeof cfg === 'object' && cfg.voucherType
              ? cfg.voucherType?._id || cfg.voucherType
              : cfg;
            if (!vt) return null;
            return { voucherType: vt, fields: cfg.fields || [] };
          })
          .filter(Boolean)
      );

      // Initialize dealer references
      setSelectedDealerRefs(
        editCompany.dealerReferences
          ? editCompany.dealerReferences.map(d => d._id || d)
          : []
      );
      setLinkedDealers(
        (editCompany.linkedDealers || []).map((ld) => ({
          company: ld.company ? (ld.company._id || ld.company) : '',
          dealer: ld.dealer ? (ld.dealer._id || ld.dealer) : '',
          name: ld.name || '',
        }))
      );
      fetchDealers();

      // Initialize additional tally items access
      setSelectedAdditionalTallyAccess(
        editCompany.additionalTallyItemsAccess
          ? editCompany.additionalTallyItemsAccess.map(c => c._id || c)
          : []
      );

      // Initialize tally field settings
      setTallyItemsFieldSettings(editCompany.tallyItemsFieldSettings || {});
      setTallyManagementAccess(editCompany.tallyManagementAccess || {
        allowParameterAssignment: true,
        allowBrandAssignment: true,
        allowImageUpload: true,
      });
      setReorderStatusFieldSettings(editCompany.reorderStatusFieldSettings || {});
      setIncludeCancelledInCalculations(editCompany.includeCancelledInCalculations || false);
      setTallyReportSettings(editCompany.tallyReportSettings || DEFAULT_TALLY_REPORT_SETTINGS);
      setManagementReportSettings(editCompany.managementReportSettings || DEFAULT_MANAGEMENT_REPORT_SETTINGS);

      if (editCompany.domains && editCompany.domains.length > 0) {
        setDomains(editCompany.domains);
      } else {
        setDomains([
          {
            domainName: editCompany.domainName || '',
            adminDashboardAccess: editCompany.adminDashboardAccess || [],
            companyHeadDashboardAccess:
              editCompany.companyHeadDashboardAccess || [],
            employeeDashboardAccess: editCompany.employeeDashboardAccess || [],
          },
        ]);
      }

      // Reset form errors
      setFormErrors({});
    } else {
      setQuotationTermsAndConditions([]);
      setDeliveryOrderTermsAndConditions([]);
      setUsageVoucherTypes([]);
      setSelectedDealerRefs([]);
      setLinkedDealers([]);
      setSelectedAdditionalTallyAccess([]);
      setTallyItemsFieldSettings({});
      setTallyManagementAccess({ allowParameterAssignment: true, allowBrandAssignment: true, allowImageUpload: true });
      setReorderStatusFieldSettings({});
      setIncludeCancelledInCalculations(false);
      setTallyReportSettings(DEFAULT_TALLY_REPORT_SETTINGS);
      setManagementReportSettings(DEFAULT_MANAGEMENT_REPORT_SETTINGS);

      setDomains([
        {
          domainName: '',
          adminDashboardAccess: [...defaultAdminAccess],
          companyHeadDashboardAccess: [...defaultCompanyHeadAccess],
          employeeDashboardAccess: [...defaultEmployeeAccess],
        },
      ]);
    }
  }, [editCompany]);

  // Fetch all dealers for dealer references multi-select
  const fetchDealers = async (filter = '') => {
    try {
      const res = await api.get('/dealers', { params: { filter, limit: 500, page: 1 } });
      setAllDealers((res.data.dealers || []).map(d => ({ value: d._id, label: `${d.businessName} (${d.phone || 'N/A'})` })));
    } catch (err) {
      console.error('Error fetching dealers:', err);
    }
  };

  const checkTallyActive = async (tallyUrl) => {
    setLoadingTally(true);
    try {
      const res = await api.post('/tally/is-tally-active', { tallyUrl });
      const isActive = res.data.active;
      if (isActive) {
        toast.success('✅ Tally server is active and responding');
      } else {
        toast.error('❌ Tally server is not responding');
      }
      return isActive;
    } catch (err) {
      toast.error('❌ Error checking Tally server status');
      Swal.fire({ icon: 'error', title: 'Error', text: 'Error checking if Tally is active.' });
      return false;
    } finally {
      setLoadingTally(false);
    }
  };

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/companies`
      );
      setCompanies(response.data);
      setFilteredCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies.');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch companies.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Search and filter functionality
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    const filtered = companies.filter(company =>
      company.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      company.email.toLowerCase().includes(searchValue.toLowerCase()) ||
      (company.firm && company.firm.firmName.toLowerCase().includes(searchValue.toLowerCase()))
    );
    setFilteredCompanies(filtered);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleEditClick = async (company) => {
    const tallyUrl = company.tallyUrl;
    setSelectedTallyName(company.TallyCompany || '');
    setSelectedTallyGUID(company.companyGUID || '');
    if (tallyUrl && tallyUrl !== '') {
      const isActive = await checkTallyActive(tallyUrl);
      if (!isActive) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Tally is not active for this company. Unable to edit.' });
        toast.warning("Tally is not active. Unable to edit.");
        setActiveTab('list');
        return;
      } else {
        setLoadingTally(true);
        try {
          const listRes = await api.post('/tally/get-tally-companies', { tallyUrl });
          const list = listRes.data.response || [];
          setTallyCompanies(list);

          if (company.companyGUID) {
            const compInTally = list.find(comp => comp.GUID === company.companyGUID);
            if (compInTally) {
              setSelectedTallyName(compInTally.NAME);
              setSelectedTallyGUID(compInTally.GUID);
            }
          }

          const currentRes = await api.post('/tally/get-current-active-company', { tallyUrl });
          setCurrentActiveCompany(currentRes.data.response);
        } catch (error) {
          console.error('Error fetching tally data:', error);
          toast.error('Failed to fetch tally data.');
        }
        setLoadingTally(false);
      }
    }

    setEditCompany(company);
    setActiveTab('form');
  };

  // Enhanced form validation with detailed error messages
  const validateForm = (event) => {
    const form = new FormData(event.target);
    const errors = {};
    let isValid = true;

    // Required field validation
    const requiredFields = {
      name: 'Company Name',
      email: 'Email',
      printName: 'Print Name',
      address: 'Address',
      phone: 'Phone',
      bankName: 'Bank Name',
      branchName: 'Branch Name',
      accountNo: 'Account Number',
      ifsc: 'IFSC Code',
      tallyUrl: 'Tally URL'
    };

    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!form.get(field)) {
        errors[field] = `${label} is required`;
        isValid = false;
      }
    });

    // Email validation
    const email = form.get('email');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // IFSC validation
    const ifsc = form.get('ifsc');
    if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      errors.ifsc = 'Please enter a valid IFSC code (e.g., SBIN0001234)';
      isValid = false;
    }

    // Tally URL validation
    const tallyUrl = form.get('tallyUrl');
    if (tallyUrl && !/^https?:\/\/.+/.test(tallyUrl)) {
      errors.tallyUrl = 'Please enter a valid URL (e.g., http://localhost:9000)';
      isValid = false;
    }

    setFormErrors(errors);

    if (!isValid) {
      toast.error('Please fix the errors in the form before submitting');
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }

    return isValid;
  };

  const buildFormData = (formData) => {
    if (!formData.has('isStockStatus')) {
      formData.append('isStockStatus', 'false');
    }
    if (!formData.has('showDeliveryOrderClosingBalance')) {
      formData.append('showDeliveryOrderClosingBalance', 'false');
    }
    if (!formData.has('showDeliveryOrderAmountToBePaid')) {
      formData.append('showDeliveryOrderAmountToBePaid', 'false');
    }
    if (!formData.has('showDeliveryOrderAdvanceLeft')) {
      formData.append('showDeliveryOrderAdvanceLeft', 'false');
    }
    if (!formData.has('isTallyMaestroEnabled')) {
      formData.append('isTallyMaestroEnabled', 'false');
    }
    if (!formData.has('tallyPanelsEnabled')) {
      formData.append('tallyPanelsEnabled', 'false');
    }
    if (!formData.has('isChoicemicEnabled')) {
      formData.append('isChoicemicEnabled', 'false');
    }
    formData.set('domainsData', JSON.stringify(domains));
    formData.set('quotationTermsAndConditions', JSON.stringify(quotationTermsAndConditions));
    formData.set('deliveryOrderTermsAndConditions', JSON.stringify(deliveryOrderTermsAndConditions));
    formData.set('dealerReferences', JSON.stringify(selectedDealerRefs));
    formData.set('linkedDealers', JSON.stringify(linkedDealers));
    formData.set('additionalTallyItemsAccess', JSON.stringify(selectedAdditionalTallyAccess));
    formData.set('usageVoucherTypes', JSON.stringify(usageVoucherTypes));
    formData.set('tallyItemsFieldSettings', JSON.stringify(tallyItemsFieldSettings));
    formData.set('tallyManagementAccess', JSON.stringify(tallyManagementAccess));
    formData.set('reorderStatusFieldSettings', JSON.stringify(reorderStatusFieldSettings));
    formData.set('tallyReportSettings', JSON.stringify(tallyReportSettings));
    formData.set('managementReportSettings', JSON.stringify(managementReportSettings));
    formData.set('includeCancelledInCalculations', includeCancelledInCalculations ? 'true' : 'false');
    return formData;
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm(event)) return;

    for (let domain of domains) {
      if (!domain.domainName) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Each domain must have a name.',
        });
        return;
      }
    }

    const formData = new FormData(event.target);

    formData.set('TallyCompany', selectedTallyName || '');
    formData.set('companyGUID', selectedTallyGUID || '');

    buildFormData(formData);

    try {
      await api.put(`/companies/${editCompany._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      // Clear form errors on success
      setFormErrors({});
      
      // Show success message with SweetAlert
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Company information has been updated successfully.',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
      
      toast.success('Company updated successfully.');
      
      // Refresh companies list
      await fetchCompanies();
      
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating company:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to update company.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check your input.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      toast.error(errorMessage);
      
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMessage,
        confirmButtonColor: '#ef4444',
      });
    }
  };

  const handleViewTallyDetails = async () => {
    setLoadingTally(true);
    
    // Show loading animation
    Swal.fire({
      title: 'Verifying Tally Connection',
      html: `
        <div class="flex flex-col items-center space-y-4">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
          <p class="text-gray-600 animate-pulse">Connecting to Tally server...</p>
          <p class="text-sm text-gray-500">Please wait while we verify the connection</p>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      customClass: {
        popup: 'animate-fadeInUp'
      }
    });

    try {
      const licenceRes = await api.post('/tally/get-lincence', { tallyUrl: editCompany.tallyUrl });
      const licenceInfo = licenceRes.data.response;

      setEditCompany(prev => ({
        ...prev,
        ADMINMAILID: licenceInfo.ADMINMAILID,
        APPLICATIONPATH: licenceInfo.APPLICATIONPATH,
        DATAPATH: licenceInfo.DATAPATH,
        ISLICCLIENTMODE: licenceInfo.ISLICCLIENTMODE === "Yes",
        ISREMOTEACCESSMODE: licenceInfo.ISREMOTEACCESSMODE === "Yes",
        SERIALNUMBER: licenceInfo.SERIALNUMBER,
      }));

      let licenceHtml = '<table style="width:100%; border-collapse:collapse; font-size: 12px;">';
      Object.keys(licenceInfo).forEach(key => {
        licenceHtml += `<tr style="border-bottom:1px solid #e5e7eb;"><td style="padding:6px 8px; font-weight:600; background:#f9fafb; color:#374151;">${key}</td><td style="padding:6px 8px; color:#6b7280;">${licenceInfo[key]}</td></tr>`;
      });
      licenceHtml += '</table>';

      // Show success with animation
      setTimeout(() => {
        Swal.fire({
          title: '<span style="color:#059669;">✅ Tally Verification Successful</span>',
          html: `
            <div class="text-left">
              <div class="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p class="text-sm font-semibold text-green-800">Selected Tally Company: ${selectedTallyName}</p>
              </div>
              <div class="max-h-80 overflow-y-auto">
                ${licenceHtml}
              </div>
            </div>
          `,
          width: '700px',
          customClass: {
            popup: 'animate-fadeInUp',
            title: 'text-lg',
            content: 'text-sm'
          },
          showClass: {
            popup: 'animate__animated animate__fadeInUp'
          }
        });
      }, 800);
    } catch (error) {
      console.error('Error fetching license details:', error);
      
      // Show error with animation
      setTimeout(() => {
        Swal.fire({
          title: '<span style="color:#dc2626;">❌ Tally Verification Failed</span>',
          html: `
            <div class="text-center">
              <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <svg class="w-12 h-12 text-red-500 mx-auto mb-2 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <p class="text-sm font-semibold text-red-800 mb-2">Connection Failed</p>
                <p class="text-sm text-red-600">Unable to connect to Tally server. Please check:</p>
                <ul class="text-xs text-red-600 mt-2 space-y-1">
                  <li>• Tally server is running</li>
                  <li>• URL is correct</li>
                  <li>• Network connectivity</li>
                </ul>
              </div>
            </div>
          `,
          customClass: {
            popup: 'animate-shakeX',
            title: 'text-lg'
          },
          showClass: {
            popup: 'animate__animated animate__shakeX'
          }
        });
      }, 500);
      
      toast.error('Failed to verify Tally connection');
    }
    setLoadingTally(false);
  };

  const renderTallyControls = () => (
    <div className="col-span-4 border p-3 bg-gray-50 rounded mt-2 animate-fadeInUp shadow-sm hover:shadow-md transition-all duration-300">
      <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
        <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Tally Integration
      </h3>
      <div className="flex items-center space-x-2 flex-wrap gap-y-2">
        <button
          type="button"
          onClick={async () => {
            setLoadingTally(true);
            try {
              const listRes = await api.post('/tally/get-tally-companies', { tallyUrl: editCompany.tallyUrl });
              const list = listRes.data.response || [];
              setTallyCompanies(list);
              const currentRes = await api.post('/tally/get-current-active-company', { tallyUrl: editCompany.tallyUrl });
              setCurrentActiveCompany(currentRes.data.response);
            } catch (error) {
              console.error('Error fetching tally companies:', error);
              
              Swal.fire({
                title: '<span style="color:#dc2626;">⚠️ Connection Error</span>',
                html: `
                  <div class="text-center">
                    <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                      <svg class="w-10 h-10 text-red-500 mx-auto mb-2 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <p class="text-sm font-semibold text-red-800">Failed to fetch Tally companies</p>
                      <p class="text-xs text-red-600 mt-1">Please verify Tally server connection</p>
                    </div>
                  </div>
                `,
                customClass: {
                  popup: 'animate-shakeX'
                },
                timer: 3000,
                timerProgressBar: true
              });
              
              toast.error('Failed to fetch Tally companies');
            }
            setLoadingTally(false);
          }}
          className={`bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md flex items-center text-xs font-medium transition-all duration-200 transform hover:scale-105 shadow-sm ${
            loadingTally ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-md'
          }`}
          disabled={loadingTally}
        >
          {loadingTally ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-2"></div>
              <span className="animate-pulse">Verifying...</span>
            </>
          ) : (
            <>
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Get Companies
            </>
          )}
        </button>

        {tallyCompanies.length > 0 && (
          <div className="flex-grow animate-slideInRight">
            <select
              value={selectedTallyGUID}
              onChange={(e) => {
                const guid = e.target.value;
                setSelectedTallyGUID(guid);
                const comp = tallyCompanies.find(c => c.GUID === guid);
                setSelectedTallyName(comp ? comp.NAME : '');
              }}
              className="border border-gray-300 p-1.5 w-full md:w-80 rounded-md text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-white hover:border-purple-400"
            >
              <option value="">Select Tally Company</option>
              {tallyCompanies.map((comp, idx) => {
                const optionValue = comp.GUID;
                const isCurrent = currentActiveCompany && comp.NAME === currentActiveCompany;
                return (
                  <option key={idx} value={optionValue} className="text-xs">
                    {comp.NAME} {isCurrent ? '(Current)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {tallyCompanies.length > 0 && (
          <button
            type="button"
            onClick={handleViewTallyDetails}
            className={`bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 transform hover:scale-105 shadow-sm animate-slideInLeft ${
              loadingTally ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-md'
            }`}
            disabled={loadingTally}
          >
            {loadingTally ? (
              <span className="animate-pulse">Loading...</span>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Details
              </>
            )}
          </button>
        )}
      </div>

      {selectedTallyName && (
        <div className="mt-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md animate-fadeIn">
          <p className="text-blue-800 text-xs flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Selected: <span className="font-semibold ml-1">{selectedTallyName}</span>
          </p>
        </div>
      )}
    </div>
  );

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm(event)) return;

    for (let domain of domains) {
      if (!domain.domainName) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Each domain must have a name.',
        });
        return;
      }
    }

    const formData = new FormData(event.target);

    buildFormData(formData);

    try {
      await api.post('/companies', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Company created successfully.');
      fetchCompanies();
      handleCloseCreateDialog();
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Failed to create company.');
    }
  };

  const handleCloseEditDialog = () => {
    setEditCompany(null);
    setTallyCompanies([]);
    setSelectedTallyName('');
    setSelectedTallyGUID('');
    setCurrentActiveCompany('');
    setActiveTab('list');
  };

  const handleCloseCreateDialog = () => {
    setEditCompany(null);
    setActiveTab('list');
  };

  const handleViewClick = (company) => {
    setViewCompany(company);
    setViewModalOpen(true);
  };

  return (
    <div
      className="p-6 animate-fadeIn container mx-auto"
      style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('form')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'form'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Create/Edit Company</span>
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Companies List</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'form' && (
        <div className="animate-fadeIn">
          {/* Create/Edit Company Form */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {editCompany ? 'Edit Company' : 'Create New Company'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {editCompany ? 'Update company information and settings' : 'Add a new company to the system'}
                  </p>
                </div>
              </div>
              {editCompany && (
                <button
                  onClick={() => {
                    setEditCompany(null);
                    setActiveTab('list');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Company Form */}
            <form 
              className="space-y-6"
              onSubmit={editCompany ? handleEditSubmit : handleCreateSubmit}
              ref={editCompany ? editFormRef : createFormRef}
            >
              {/* Error Summary */}
              {Object.keys(formErrors).length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-red-800 font-semibold">Please fix the following errors:</h4>
                  </div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {Object.entries(formErrors).map(([field, error]) => (
                      <li key={field} className="flex items-center">
                        <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Basic Company Information */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  Company Information
                  <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Company Name *</label>
                    <input
                      name="name"
                      defaultValue={editCompany?.name || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Email *</label>
                    <input
                      name="email"
                      type="email"
                      defaultValue={editCompany?.email || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Print Name *</label>
                    <input
                      name="printName"
                      defaultValue={editCompany?.printName || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter print name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Phone *</label>
                    <input
                      name="phone"
                      defaultValue={editCompany?.phone || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block font-medium text-gray-700">Address *</label>
                    <textarea
                      name="address"
                      defaultValue={editCompany?.address || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      rows="2"
                      placeholder="Enter company address"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">GST Number</label>
                    <input
                      name="gstNo"
                      defaultValue={editCompany?.gstNo || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter GST number (optional)"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isStockStatus"
                        defaultChecked={editCompany?.isStockStatus || false}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="block font-medium text-gray-700">Enable Stock Management</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="showDeliveryOrderClosingBalance"
                        defaultChecked={editCompany?.showDeliveryOrderClosingBalance || false}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="block font-medium text-gray-700">Show Closing Balance on Delivery Order Print</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="showDeliveryOrderAmountToBePaid"
                        defaultChecked={editCompany?.showDeliveryOrderAmountToBePaid || false}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="block font-medium text-gray-700">Show Amount to be Paid on Delivery Order Print</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="showDeliveryOrderAdvanceLeft"
                        defaultChecked={editCompany?.showDeliveryOrderAdvanceLeft || false}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="block font-medium text-gray-700">Show Advance Left on Delivery Order Print</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isTallyMaestroEnabled"
                        defaultChecked={editCompany?.isTallyMaestroEnabled || false}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="block font-medium text-gray-700">Enable Tally Maestro App</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="tallyPanelsEnabled"
                        defaultChecked={editCompany?.tallyPanelsEnabled !== false}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="block font-medium text-gray-700">Enable Display Panels</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isChoicemicEnabled"
                        defaultChecked={editCompany?.isChoicemicEnabled || false}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="block font-medium text-gray-700">Enable ChoiceMic App</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={includeCancelledInCalculations}
                        onChange={(e) => setIncludeCancelledInCalculations(e.target.checked)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="block font-medium text-gray-700">Include Cancelled in Calculations</span>
                    </label>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block font-medium text-gray-700">Additional Tally Items Access</label>
                    <Select
                      isMulti
                      options={companies
                        .filter(c => c._id !== editCompany?._id)
                        .map(c => ({ value: c._id, label: `${c.name} (${c.id || ''})` }))}
                      value={companies
                        .filter(c => selectedAdditionalTallyAccess.includes(c._id))
                        .map(c => ({ value: c._id, label: `${c.name} (${c.id || ''})` }))}
                      onChange={(selected) => setSelectedAdditionalTallyAccess(selected ? selected.map(s => s.value) : [])}
                      placeholder="Select sibling companies for tally items cross-access..."
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isClearable
                    />
                    <p className="text-xs text-gray-500">Employees of this company can view tally items from selected companies in Tally Maestro</p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block font-medium text-gray-700">Dealer References</label>
                    <Select
                      isMulti
                      options={allDealers}
                      value={allDealers.filter(d => selectedDealerRefs.includes(d.value))}
                      onChange={(selected) => setSelectedDealerRefs(selected ? selected.map(s => s.value) : [])}
                      placeholder="Search and select dealers..."
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isClearable
                      onInputChange={(val) => { if (val.length >= 2) fetchDealers(val); }}
                    />
                  </div>
                </div>
              </div>

              {/* Linked Dealers (Tally Reports) */}
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">Linked Dealers (Tally Reports)</h4>
                    <p className="text-xs text-gray-500">
                      Attribute sales in tally reports to a linked company's dealer. The name is the report label (e.g. RLU-2 SALES).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLinkedDealers([...linkedDealers, { company: '', dealer: '', name: '' }])}
                    className="text-xs px-2 py-1 bg-indigo-600 text-white rounded"
                  >
                    + Add Linked Dealer
                  </button>
                </div>
                {linkedDealers.length === 0 && <p className="text-xs text-gray-500">No linked dealers.</p>}
                <div className="space-y-2">
                  {linkedDealers.map((ld, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <select
                        value={ld.company || ''}
                        onChange={(e) =>
                          setLinkedDealers(linkedDealers.map((x, i) => (i === idx ? { ...x, company: e.target.value } : x)))
                        }
                        className="col-span-4 p-2 border rounded text-sm"
                      >
                        <option value="">Select company</option>
                        {companies.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                      </select>
                      <select
                        value={ld.dealer || ''}
                        onChange={(e) =>
                          setLinkedDealers(linkedDealers.map((x, i) => (i === idx ? { ...x, dealer: e.target.value } : x)))
                        }
                        className="col-span-4 p-2 border rounded text-sm"
                      >
                        <option value="">Select dealer</option>
                        {allDealers.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={ld.name || ''}
                        onChange={(e) =>
                          setLinkedDealers(linkedDealers.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))
                        }
                        placeholder="Report name (e.g. RLU-2 SALES)"
                        className="col-span-3 p-2 border rounded text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setLinkedDealers(linkedDealers.filter((_, i) => i !== idx))}
                        className="col-span-1 px-2 py-1 bg-red-500 text-white rounded text-xs"
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Banking Information */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  Banking Details
                  <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Bank Name *</label>
                    <input
                      name="bankName"
                      defaultValue={editCompany?.bankName || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Branch Name *</label>
                    <input
                      name="branchName"
                      defaultValue={editCompany?.branchName || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter branch name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Account Number *</label>
                    <input
                      name="accountNo"
                      defaultValue={editCompany?.accountNo || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter account number"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">IFSC Code *</label>
                    <input
                      name="ifsc"
                      defaultValue={editCompany?.ifsc || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    />
                  </div>
                </div>
              </div>

              {/* File Uploads */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-purple-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  File Uploads
                  <span className="ml-auto text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">{editCompany ? 'Optional' : 'Required'}</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Company Logo {!editCompany && '*'}</label>
                    <input
                      type="file"
                      name="logo"
                      accept="image/*"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required={!editCompany}
                    />
                    {editCompany && (
                      <p className="text-xs text-gray-500">Leave empty to keep existing logo</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Bank QR Code {!editCompany && '*'}</label>
                    <input
                      type="file"
                      name="bankQR"
                      accept="image/*"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required={!editCompany}
                    />
                    <input
                      type="text"
                      name="qrName"
                      defaultValue={editCompany?.qrName || ''}
                      placeholder="QR Code name/description"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-2"
                    />
                    {editCompany && (
                      <p className="text-xs text-gray-500">Leave empty to keep existing QR code</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Public Catalog URL</label>
                    <input
                      type="url"
                      name="publicUrl"
                      defaultValue={editCompany?.publicUrl || ''}
                      placeholder="https://items.example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500">
                      Base URL for public item QR codes. QR will encode {`{publicUrl}/items/{id}`}. Leave empty to keep QR codes as raw item IDs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-yellow-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    </svg>
                  </div>
                  Social Media & Website
                  <span className="ml-auto text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Optional</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Website URL</label>
                    <input
                      name="website"
                      type="url"
                      defaultValue={editCompany?.website || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://www.example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">LinkedIn URL</label>
                    <input
                      name="linkedin"
                      type="url"
                      defaultValue={editCompany?.linkedin || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://linkedin.com/company/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Facebook URL</label>
                    <input
                      name="facebook"
                      type="url"
                      defaultValue={editCompany?.facebook || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://facebook.com/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Instagram URL</label>
                    <input
                      name="instagram"
                      type="url"
                      defaultValue={editCompany?.instagram || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://instagram.com/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Twitter URL</label>
                    <input
                      name="twitter"
                      type="url"
                      defaultValue={editCompany?.twitter || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://twitter.com/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">WhatsApp Business</label>
                    <input
                      name="whatsappBusiness"
                      defaultValue={editCompany?.whatsappBusiness || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="WhatsApp Business number or link"
                    />
                  </div>
                </div>
              </div>

              {/* Tally Integration */}
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-indigo-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  Tally Integration
                  <span className="ml-auto text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Tally URL *</label>
                    <input
                      name="tallyUrl"
                      defaultValue={editCompany?.tallyUrl || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                      placeholder="http://localhost:9000 or http://your-tally-server:9000"
                    />
                  </div>
                  
                  {/* Tally Controls - Only show if editing */}
                  {editCompany && renderTallyControls()}
                  
                  {/* Hidden fields for Tally data */}
                  <input type="hidden" name="TallyCompany" value={selectedTallyName} />
                  <input type="hidden" name="companyGUID" value={selectedTallyGUID} />
                  <input type="hidden" name="ADMINMAILID" value={editCompany?.ADMINMAILID || ''} />
                  <input type="hidden" name="APPLICATIONPATH" value={editCompany?.APPLICATIONPATH || ''} />
                  <input type="hidden" name="DATAPATH" value={editCompany?.DATAPATH || ''} />
                  <input type="hidden" name="ISLICCLIENTMODE" value={editCompany?.ISLICCLIENTMODE ? 'on' : 'off'} />
                  <input type="hidden" name="ISREMOTEACCESSMODE" value={editCompany?.ISREMOTEACCESSMODE ? 'on' : 'off'} />
                  <input type="hidden" name="SERIALNUMBER" value={editCompany?.SERIALNUMBER || ''} />
                </div>
              </div>

              {/* Tally Maestro Settings */}
              {editCompany && (
                <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-6 h-6 bg-cyan-500 rounded-md flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    Tally Items Field Settings
                    <span className="ml-auto text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full">Display</span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[
                      { key: 'showCategory', label: 'Category' },
                      { key: 'showBrand', label: 'Brand' },
                      { key: 'showHsnCode', label: 'HSN Code' },
                      { key: 'showPartNo', label: 'Part No' },
                      { key: 'showBaseUnits', label: 'Base Units' },
                      { key: 'showAdditionalUnits', label: 'Additional Units' },
                      { key: 'showStockSummary', label: 'Stock Summary' },
                      { key: 'showCalculatedStock', label: 'Calculated Stock' },
                      { key: 'showPricing', label: 'Pricing' },
                      { key: 'showParameterPricing', label: 'Parameter Pricing' },
                      { key: 'showParameters', label: 'Parameters' },
                      { key: 'showDimensions', label: 'Dimensions' },
                      { key: 'showCoverage', label: 'Coverage' },
                      { key: 'showPiecesPerBox', label: 'Pieces Per Box' },
                      { key: 'showTaxInfo', label: 'Tax Info' },
                      { key: 'showGstDetails', label: 'GST Details' },
                      { key: 'showTechnicalDetails', label: 'Technical Details' },
                      { key: 'showGuid', label: 'GUID' },
                      { key: 'showAlterId', label: 'AlterID' },
                      { key: 'showParent', label: 'Parent' },
                      { key: 'showSection', label: 'Section' },
                      { key: 'showTimestamps', label: 'Timestamps' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md border border-gray-200 hover:border-cyan-300 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tallyItemsFieldSettings[key] !== undefined ? tallyItemsFieldSettings[key] : true}
                          onChange={(e) => setTallyItemsFieldSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Tally Management Access */}
              {editCompany && (
                <div className="bg-violet-50 p-4 rounded-lg border border-violet-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-6 h-6 bg-violet-500 rounded-md flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    Tally Management Access Controls
                    <span className="ml-auto text-xs bg-violet-100 text-violet-800 px-2 py-1 rounded-full">Permissions</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: 'allowParameterAssignment', label: 'Allow Parameter Assignment' },
                      { key: 'allowBrandAssignment', label: 'Allow Brand Assignment' },
                      { key: 'allowImageUpload', label: 'Allow Image Upload' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center space-x-2 bg-white px-4 py-3 rounded-md border border-gray-200 hover:border-violet-300 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tallyManagementAccess[key] !== undefined ? tallyManagementAccess[key] : true}
                          onChange={(e) => setTallyManagementAccess(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Reorder Status Field Settings */}
              {editCompany && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-6 h-6 bg-amber-500 rounded-md flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    Reorder Status Field Settings
                    <span className="ml-auto text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">Stock</span>
                  </h4>
                  <div className="space-y-3">
                    {[
                      { prefix: 'purchaseOrder', label: 'Purchase Order' },
                      { prefix: 'salesOrderDue', label: 'Sales Order Due' },
                      { prefix: 'netAvailable', label: 'Net Available' },
                      { prefix: 'reorderLevel', label: 'Reorder Level' },
                      { prefix: 'shortFall', label: 'Short Fall' },
                      { prefix: 'minimumReorderQty', label: 'Minimum Reorder Qty' },
                      { prefix: 'orderToBePlaced', label: 'Order To Be Placed' },
                    ].map(({ prefix, label }) => {
                      const showKey = `show${prefix.charAt(0).toUpperCase() + prefix.slice(1)}`;
                      const nameKey = `${prefix}DisplayName`;
                      const sqftKey = `${prefix}UseSqft`;
                      return (
                        <div key={prefix} className="flex items-center gap-4 bg-white px-4 py-2 rounded-md border border-gray-200">
                          <label className="flex items-center space-x-2 min-w-[160px]">
                            <input
                              type="checkbox"
                              checked={reorderStatusFieldSettings[showKey] || false}
                              onChange={(e) => setReorderStatusFieldSettings(prev => ({ ...prev, [showKey]: e.target.checked }))}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{label}</span>
                          </label>
                          <input
                            type="text"
                            value={reorderStatusFieldSettings[nameKey] || label}
                            onChange={(e) => setReorderStatusFieldSettings(prev => ({ ...prev, [nameKey]: e.target.value }))}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-amber-500"
                            placeholder="Display name"
                          />
                          <label className="flex items-center space-x-1">
                            <input
                              type="checkbox"
                              checked={reorderStatusFieldSettings[sqftKey] || false}
                              onChange={(e) => setReorderStatusFieldSettings(prev => ({ ...prev, [sqftKey]: e.target.checked }))}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-xs text-gray-600">Sqft</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Domain Configuration */}
              {(editCompany || !editCompany) && (
                <DomainAccessManager 
                  domains={domains} 
                  setDomains={setDomains} 
                  defaultAccess={{
                    admin: defaultAdminAccess,
                    companyHead: defaultCompanyHeadAccess,
                    employee: defaultEmployeeAccess
                  }} 
                />
              )}

              {/* Voucher Types for Tally Item Usage */}
              {editCompany && (
                <UsageVoucherTypeSelector
                  companyId={editCompany._id}
                  usageVoucherTypes={usageVoucherTypes}
                  onChange={setUsageVoucherTypes}
                />
              )}

              {/* Tally Reports configuration */}
              <TallyReportSettingsManager
                companyId={editCompany?._id}
                value={tallyReportSettings}
                onChange={setTallyReportSettings}
              />

              {/* Management Reports configuration */}
              <ManagementReportSettingsManager
                value={managementReportSettings}
                onChange={setManagementReportSettings}
              />

              {/* Terms and Conditions */}
              <TermsAndConditionsManager
                title="Quotation Terms and Conditions"
                terms={quotationTermsAndConditions}
                setTerms={setQuotationTermsAndConditions}
                placeholder="Enter quotation term or condition"
              />

              <TermsAndConditionsManager
                title="Delivery Order Terms and Conditions"
                terms={deliveryOrderTermsAndConditions}
                setTerms={setDeliveryOrderTermsAndConditions}
                placeholder="Enter delivery order term or condition"
              />

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setEditCompany(null);
                    setActiveTab('list');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editCompany ? 'Update Company' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="animate-fadeIn">
          {/* Header for List Tab */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Companies List</h3>
                <p className="text-sm text-gray-600">View and manage all companies</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditCompany(null);
                setActiveTab('form');
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Create Company</span>
            </button>
          </div>

          {/* Loading State with Better Animation */}
      {isLoading && (
        <div className="w-full text-center p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl mb-6 animate-pulse">
          <div className="flex items-center justify-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-blue-200 rounded-full"></div>
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-blue-700">
              <p className="font-medium text-lg">Loading companies...</p>
              <p className="text-sm opacity-75">Please wait while we fetch the data</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      {!isLoading && companies.length > 0 && (
        <div className="mb-4 animate-slideUp">
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search companies by name, email, or firm..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md text-sm"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => handleSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              Found {filteredCompanies.length} of {companies.length} companies
            </div>
          )}
        </div>
      )}

      {/* Enhanced Table with Better Styling */}
      <div className="overflow-hidden rounded-xl shadow-lg border border-gray-200 animate-slideUp">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            {/* Enhanced Header */}
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Logo</span>
                  </div>
                </th>
                <th className="group px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>Company Name</span>
                  </div>
                </th>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <span>Email</span>
                  </div>
                </th>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Firm</span>
                  </div>
                </th>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            
            {/* Enhanced Body with Staggered Animation */}
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredCompanies.map((company, index) => (
                <tr
                  key={company._id}
                  className="group hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 transform hover:scale-[1.01] animate-fadeInUp border-l-4 border-transparent hover:border-blue-500"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  {/* Logo Column with Enhanced Styling */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="relative group">
                      <div className="w-12 h-12 rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-300 border-2 border-gray-200 group-hover:border-blue-300">
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/companies/image/${company._id}`}
                          alt={`${company.name} logo`}
                          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/50?text=Logo';
                          }}
                        />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </td>
                  
                  {/* Company Name with Better Typography */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="text-xs font-bold text-gray-900 group-hover:text-blue-800 transition-colors duration-200">
                      {company.name}
                    </div>
                    <div className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors duration-200">
                      ID: {company.id || company._id.slice(-6)}
                    </div>
                  </td>
                  
                  {/* Email with Better Styling */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="text-xs text-gray-700 group-hover:text-gray-900 transition-colors duration-200 flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      <span className="truncate max-w-xs">{company.email}</span>
                    </div>
                  </td>
                  
                  {/* Firm with Enhanced Badge */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    {company.firm ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200 group-hover:from-blue-200 group-hover:to-indigo-200 transition-all duration-300 shadow-sm">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {company.firm.firmName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        No firm
                      </span>
                    )}
                  </td>
                  
                  {/* Enhanced Action Buttons */}
                  <td className="px-4 py-2 whitespace-nowrap text-xs font-medium">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEditClick(company)}
                        className="group/btn inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-green-500 transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <svg className="w-3 h-3 mr-1 group-hover/btn:rotate-12 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewClick(company)}
                        className="group/btn inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <svg className="w-3 h-3 mr-1 group-hover/btn:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Table Footer with Company Count */}
        {filteredCompanies.length > 0 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>
                    {searchTerm ? (
                      <>Showing <span className="font-semibold text-gray-800">{filteredCompanies.length}</span> of <span className="font-semibold text-gray-800">{companies.length}</span> companies</>
                    ) : (
                      <>Total Companies: <span className="font-semibold text-gray-800">{companies.length}</span></>
                    )}
                  </span>
                </div>
                {searchTerm && (
                  <div className="flex items-center space-x-1 text-blue-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span className="text-xs font-medium">Filtered</span>
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {!isLoading && companies.length === 0 && (
        <div className="text-center py-12 animate-fadeIn">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
          <p className="text-gray-500 mb-6">Get started by creating your first company.</p>
          <button
            onClick={() => {
              setEditCompany(null);
              setActiveTab('form');
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Create First Company
          </button>
        </div>
      )}

      {/* No Search Results State */}
      {!isLoading && companies.length > 0 && filteredCompanies.length === 0 && searchTerm && (
        <div className="text-center py-12 animate-fadeIn">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies match your search</h3>
          <p className="text-gray-500 mb-6">Try searching with different keywords or clear the search filter.</p>
          <button
            onClick={() => handleSearch('')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Clear Search
          </button>
        </div>
      )}
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        
        .animate-slideDown {
          animation: slideDown 0.8s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s ease-out;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out both;
        }
      `}</style>


        </div>
      )}

      <ViewCompanyModal 
        company={viewCompany}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
      
      {/* Enhanced CSS Styles */}
      <style>{`
        /* Form input focus effects */
        .form-input:focus {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
        
        /* Button hover effects */
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(59, 130, 246, 0.3);
        }
        
        .btn-secondary:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        
        /* Table row hover effects */
        .table-row:hover {
          transform: translateX(5px);
          box-shadow: 5px 0 15px rgba(0, 0, 0, 0.1);
        }
        
        /* Loading spinner enhancement */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .loading-spinner {
          animation: spin 1s linear infinite;
        }
        
        /* Modal backdrop blur */
        .modal-backdrop {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        
        /* Smooth transitions for all interactive elements */
        * {
          transition: all 0.2s ease-in-out;
        }
        
        /* Custom scrollbar for modals */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        
        /* Gradient text effects */
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        /* Floating labels effect */
        .floating-label {
          transform: translateY(-25px) scale(0.875);
          color: #3b82f6;
        }
        
        /* Card hover effects */
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
        }
        
        /* Progress bar animation */
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        
        .progress-bar {
          animation: progress 2s ease-in-out;
        }
        
        /* Pulse effect for important elements */
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .pulse-effect {
          animation: pulse 2s infinite;
        }
        
        /* Slide in effects */
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .slide-in-left {
          animation: slideInLeft 0.6s ease-out;
        }
        
        .slide-in-right {
          animation: slideInRight 0.6s ease-out;
        }
        
        /* Enhanced focus rings */
        .focus-ring:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
        }
        
        /* Ripple effect on click */
        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }
        
        .ripple-effect {
          position: relative;
          overflow: hidden;
        }
        
        .ripple-effect::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }
        
        .ripple-effect:active::before {
          width: 300px;
          height: 300px;
        }
      `}</style>
    </div>
  );
};

export default CompanyManagement;